import AMapLoader from "@amap/amap-jsapi-loader";
import { Check, Crosshair, LocateFixed, MapPinPlus, Pencil, PencilRuler, RefreshCw, Search, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { gcj02ToWgs84, wgs84ToGcj02 } from "../lib/coordinates";
import { buildElevationPeaks, sampleRoutePath } from "../lib/elevation-profile";
import { getRouteCooldownMs, requestRoute, routeRequestKey, RoutePlanError, type RouteResult } from "../lib/route-request-gate";
import { api } from "../services/api";
import type { Day, ElevationFocus, Navigation, NavigationSegment, Place, PoiCandidate, PublicConfig, TripDocument, TravelMode } from "../types";

declare global {
  interface Window {
    _AMapSecurityConfig?: { serviceHost?: string };
  }
}

interface Props {
  config: PublicConfig | null;
  document: TripDocument;
  day: Day | null;
  selectedPlaceId: string | null;
  viewScope: "day" | "all";
  elevationFocus: ElevationFocus | null;
  onViewScopeChange: (scope: "day" | "all") => void;
  onElevationFocus: (focus: ElevationFocus | null) => void;
  onSelectPlace: (id: string | null) => void;
  onAddPlace: (place: Place) => void;
  onMovePlace: (id: string, place: Partial<Place>) => void;
  onSetNavigation: (dayId: string, value: Navigation) => void;
  canEdit: boolean;
  showSearch?: boolean;
}

type AMapValue = any;

export function MapCanvas({ config, document, day, selectedPlaceId, viewScope, elevationFocus, onViewScopeChange, onElevationFocus, onSelectPlace, onAddPlace, onMovePlace, onSetNavigation, canEdit, showSearch = true }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<AMapValue>(null);
  const amapRef = useRef<AMapValue>(null);
  const overlaysRef = useRef<AMapValue[]>([]);
  const elevationMarkerRef = useRef<AMapValue>(null);
  const clickHandlerRef = useRef<((event: AMapValue) => void) | null>(null);
  const editableLineRef = useRef<AMapValue>(null);
  const polylineEditorRef = useRef<AMapValue>(null);
  const [ready, setReady] = useState(false);
  const [mapError, setMapError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<PoiCandidate[]>([]);
  const [searching, setSearching] = useState(false);
  const [addingPoint, setAddingPoint] = useState(false);
  const [planning, setPlanning] = useState(false);
  const [searchScope, setSearchScope] = useState<"global" | "viewport">("viewport");
  const [editingTrack, setEditingTrack] = useState(false);
  const [hasEditableLine, setHasEditableLine] = useState(false);
  const [routePlanVersion, setRoutePlanVersion] = useState(0);
  const [planProgress, setPlanProgress] = useState(0);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const searchSerialRef = useRef(0);
  const planRunRef = useRef(0);
  const elevationRunsRef = useRef<Record<string, number>>({});
  const activeElevationPathsRef = useRef<Record<string, string>>({});
  const documentRef = useRef(document);
  documentRef.current = document;
  const routeSignaturesRef = useRef<Record<string, string>>({});
  const routeSignature = useMemo(() => day ? [day.mode ?? "driving", ...day.stops.map((stop) => {
    const coord = document.trip.nodes[stop.node]?.coord;
    return `${stop.node}@${coord?.[0].toFixed(6)},${coord?.[1].toFixed(6)}`;
  })].join("|") : "", [day, document.trip.nodes]);

  useEffect(() => {
    if (!config || !containerRef.current || !config.amapKey) {
      if (config && !config.amapKey) setMapError("尚未配置高德 Web Key，行程编辑仍可使用");
      return;
    }
    let active = true;
    window._AMapSecurityConfig = { serviceHost: new URL(config.amapServiceHost, window.location.origin).href.replace(/\/$/, "") };
    AMapLoader.load({
      key: config.amapKey,
      version: "2.0",
      plugins: ["AMap.Scale", "AMap.Geocoder", "AMap.PlaceSearch", "AMap.Driving", "AMap.Walking", "AMap.Riding", "AMap.PolylineEditor"]
    }).then((AMap) => {
      if (!active || !containerRef.current) return;
      amapRef.current = AMap;
      const first = Object.values(document.trip.nodes)[0];
      const map = new AMap.Map(containerRef.current, {
        viewMode: "2D",
        zoom: 7,
        center: first ? wgs84ToGcj02(first.coord) : [101.9, 30.05],
        mapStyle: "amap://styles/whitesmoke",
        showLabel: true,
        resizeEnable: true
      });
      map.addControl(new AMap.Scale({ position: "LB" }));
      mapRef.current = map;
      map.on("complete", () => active && setReady(true));
    }).catch(() => active && setMapError("地图加载失败，请检查 Key、代理与网络设置"));
    return () => {
      active = false;
      polylineEditorRef.current?.close();
      if (elevationMarkerRef.current) mapRef.current?.remove(elevationMarkerRef.current);
      elevationMarkerRef.current = null;
      mapRef.current?.destroy();
      mapRef.current = null;
      amapRef.current = null;
    };
  }, [config?.amapKey, config?.amapServiceHost]);

  const reverseGeocode = useCallback((position: [number, number], callback: (address: string) => void) => {
    const AMap = amapRef.current;
    if (!AMap) return callback("");
    const geocoder = new AMap.Geocoder({ extensions: "all" });
    geocoder.getAddress(position, (status: string, result: AMapValue) => {
      callback(status === "complete" ? result.regeocode?.formattedAddress ?? "" : "");
    });
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    const AMap = amapRef.current;
    if (!map || !AMap || !ready) return;
    polylineEditorRef.current?.close();
    polylineEditorRef.current = null;
    editableLineRef.current = null;
    setHasEditableLine(false);
    setEditingTrack(false);
    if (overlaysRef.current.length) map.remove(overlaysRef.current);
    overlaysRef.current = [];
    const visibleDays = viewScope === "all" ? document.trip.days : day ? [day] : [];
    const seenPlaces = new Set<string>();
    const markerOverlays = visibleDays.flatMap((visibleDay) => visibleDay.stops.flatMap((stop, index) => {
      if (seenPlaces.has(stop.node)) return [];
      seenPlaces.add(stop.node);
      const place = document.trip.nodes[stop.node];
      if (!place) return [];
      const root = globalThis.document.createElement("button");
      root.type = "button";
      const compact = viewScope === "all";
      const isStart = index === 0;
      const isEnd = index === visibleDay.stops.length - 1;
      const elevation = Number.isFinite(place.elevation) ? `${Math.round(place.elevation!)} m` : "";
      const markerColor = visibleDay.color || document.trip.accent || "#3977A8";
      root.className = `map-marker map-marker--${compact ? "all" : "day"}${isStart ? " is-start" : ""}${isEnd ? " is-end" : ""}${selectedPlaceId === stop.node ? " is-selected" : ""}`;
      root.dataset.placeId = stop.node;
      root.style.setProperty("--marker-color", markerColor);
      root.setAttribute("aria-label", `${index + 1}. ${place.name}`);
      const dot = globalThis.document.createElement("span");
      dot.className = "map-marker__dot";
      dot.textContent = compact ? "" : String(index + 1);
      const label = globalThis.document.createElement("span");
      label.className = "map-marker__label";
      const name = globalThis.document.createElement("strong");
      name.textContent = place.name;
      const meta = globalThis.document.createElement("small");
      meta.textContent = compact ? elevation : [isStart ? "出发地" : isEnd ? "当天终点" : stop.tags?.includes("住宿点") ? "住宿点" : "途经点", elevation].filter(Boolean).join(" · ");
      label.append(name, meta);
      root.append(dot, label);
      if (place.elevation !== undefined) {
        const profile = document.navigation[`${document.trip.id}::${visibleDay.id}`]?.profile ?? [];
        const nearest = nearestProfilePoint(profile, place.coord);
        root.addEventListener("mouseenter", () => onElevationFocus({ dayId: visibleDay.id, placeId: stop.node, coord: place.coord, elevation: place.elevation!, distanceKm: nearest?.distanceKm ?? 0 }));
        root.addEventListener("mouseleave", () => onElevationFocus(null));
      }
      const draggable = canEdit && visibleDay.id === day?.id;
      const marker = new AMap.Marker({ position: wgs84ToGcj02(place.coord), content: root, offset: new AMap.Pixel(compact ? -4 : -15, compact ? -11 : -15), draggable, cursor: draggable ? "grab" : "pointer", zIndex: selectedPlaceId === stop.node ? 160 : compact ? 105 : 120 });
      marker.on("click", () => onSelectPlace(stop.node));
      if (draggable) marker.on("dragend", (event: AMapValue) => {
        const position: [number, number] = [event.lnglat.getLng(), event.lnglat.getLat()];
        reverseGeocode(position, (address) => {
          onMovePlace(stop.node, { coord: gcj02ToWgs84(position), amapAddress: address || place.amapAddress, positionSource: "地图拖拽校准", uncertain: false });
        });
      });
      return [marker];
    }));

    const routeOverlays = visibleDays.flatMap((visibleDay) => {
      const navigation = document.navigation[`${document.trip.id}::${visibleDay.id}`];
      const path = navigation?.path?.length ? navigation.path.map(wgs84ToGcj02) : visibleDay.stops.map((stop) => document.trip.nodes[stop.node]).filter(Boolean).map((place) => wgs84ToGcj02(place.coord));
      if (path.length < 2) return [];
      const active = visibleDay.id === day?.id;
      const primary = new AMap.Polyline({ path, strokeColor: visibleDay.color || document.trip.accent || "#3977A8", strokeWeight: active ? 5 : 3, strokeOpacity: active ? 0.96 : 0.42, lineJoin: "round", lineCap: "round", showDir: active, zIndex: active ? 80 : 50 });
      if (active) editableLineRef.current = primary;
      return [
        new AMap.Polyline({ path, strokeColor: "#FBFCF8", strokeWeight: active ? 9 : 7, strokeOpacity: active ? 0.92 : 0.55, lineJoin: "round", lineCap: "round", zIndex: active ? 70 : 45 }),
        primary
      ];
    });
    overlaysRef.current = [...routeOverlays, ...markerOverlays];
    map.add(overlaysRef.current);
    setHasEditableLine(!!editableLineRef.current);
  }, [canEdit, day, document, ready, selectedPlaceId, onElevationFocus, onMovePlace, onSelectPlace, reverseGeocode, viewScope]);

  useEffect(() => {
    containerRef.current?.querySelectorAll<HTMLElement>(".map-marker[data-place-id]").forEach((marker) => {
      marker.classList.toggle("is-elevation-focused", marker.dataset.placeId === elevationFocus?.placeId);
    });
  }, [elevationFocus?.placeId, ready, viewScope]);

  useEffect(() => {
    const map = mapRef.current;
    const AMap = amapRef.current;
    if (!map || !AMap || !ready) return;
    if (!elevationFocus?.coord || elevationFocus.placeId) {
      if (elevationMarkerRef.current) map.remove(elevationMarkerRef.current);
      elevationMarkerRef.current = null;
      return;
    }
    if (elevationMarkerRef.current) {
      elevationMarkerRef.current.setPosition(wgs84ToGcj02(elevationFocus.coord));
      const label = elevationMarkerRef.current.getContent()?.querySelector?.("strong");
      if (label) label.textContent = `${Math.round(elevationFocus.elevation)} m`;
      return;
    }
    const content = globalThis.document.createElement("div");
    content.className = "map-elevation-focus";
    content.innerHTML = `<span></span><strong>${Math.round(elevationFocus.elevation)} m</strong>`;
    const marker = new AMap.Marker({ position: wgs84ToGcj02(elevationFocus.coord), content, offset: new AMap.Pixel(-8, -8), zIndex: 220 });
    if (elevationMarkerRef.current) map.remove(elevationMarkerRef.current);
    elevationMarkerRef.current = marker;
    map.add(marker);
  }, [elevationFocus, ready]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready || !overlaysRef.current.length) return;
    map.setFitView(overlaysRef.current, false, [96, 96, 96, 96], 12);
  }, [day?.id, ready, viewScope]);

  useEffect(() => {
    const map = mapRef.current;
    const AMap = amapRef.current;
    if (!map || !AMap) return;
    if (clickHandlerRef.current) map.off("click", clickHandlerRef.current);
    if (!addingPoint) return;
    const handler = (event: AMapValue) => {
      const position: [number, number] = [event.lnglat.getLng(), event.lnglat.getLat()];
      reverseGeocode(position, (address) => {
        onAddPlace({ name: address ? address.split(/[省市区县]/).filter(Boolean).at(-1)?.slice(0, 24) || "地图地点" : "地图地点", coord: gcj02ToWgs84(position), amapAddress: address, positionSource: "高德地图点选（GCJ-02 转 WGS-84）", uncertain: true });
      });
      setAddingPoint(false);
    };
    clickHandlerRef.current = handler;
    map.on("click", handler);
    return () => map.off("click", handler);
  }, [addingPoint, onAddPlace, reverseGeocode]);

  useEffect(() => {
    const cancel = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setAddingPoint(false);
      setResults([]);
    };
    window.addEventListener("keydown", cancel);
    return () => window.removeEventListener("keydown", cancel);
  }, []);

  const search = useCallback((term = query) => {
    const AMap = amapRef.current;
    const normalized = term.trim();
    if (!AMap || normalized.length < 2) return;
    const serial = ++searchSerialRef.current;
    setSearching(true);
    const service = new AMap.PlaceSearch({ pageSize: 8, pageIndex: 1, extensions: "all" });
    const done = (status: string, result: AMapValue) => {
      if (serial !== searchSerialRef.current) return;
      const pois = status === "complete" ? result.poiList?.pois ?? [] : [];
      const candidates = pois.filter((poi: AMapValue) => poi.location).map((poi: AMapValue) => ({ id: poi.id, name: poi.name, address: Array.isArray(poi.address) ? poi.address.join("") : poi.address || [poi.pname, poi.cityname, poi.adname].filter(Boolean).join(""), type: poi.type, location: [poi.location.lng, poi.location.lat] }));
      setResults(candidates);
      if (!candidates.length) setMapError(status === "complete" ? "没有找到匹配地点，请尝试更具体的名称" : "地点搜索暂时不可用，请检查地图服务配置");
      else setMapError(null);
      setSearching(false);
    };
    if (searchScope === "viewport" && mapRef.current) service.searchNearBy(normalized, mapRef.current.getCenter(), 50000, done);
    else service.search(normalized, done);
  }, [query, searchScope]);

  useEffect(() => {
    if (query.trim().length < 2) {
      setResults([]);
      return;
    }
    const timer = window.setTimeout(() => search(query), 450);
    return () => window.clearTimeout(timer);
  }, [query, search]);

  const choosePoi = (poi: PoiCandidate) => {
    if (canEdit) onAddPlace({ name: poi.name, coord: gcj02ToWgs84(poi.location), amapAddress: poi.address, positionSource: "高德地图 POI（GCJ-02 转 WGS-84）", uncertain: false });
    mapRef.current?.setZoomAndCenter(15, poi.location);
    setResults([]);
    setQuery("");
  };

  const fit = () => mapRef.current?.setFitView(overlaysRef.current, false, [96, 96, 96, 96], 12);

  const locate = () => {
    if (!navigator.geolocation) return setMapError("当前浏览器不支持定位");
    navigator.geolocation.getCurrentPosition(
      (position) => mapRef.current?.setZoomAndCenter(15, wgs84ToGcj02([position.coords.latitude, position.coords.longitude])),
      () => setMapError("无法读取当前位置，请检查浏览器定位权限"),
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 }
    );
  };

  const toggleTrackEditing = () => {
    const map = mapRef.current;
    const AMap = amapRef.current;
    const line = editableLineRef.current;
    if (!map || !AMap || !line || !day) return;
    if (polylineEditorRef.current) {
      polylineEditorRef.current.close();
      const path = (line.getPath() ?? []).map((point: AMapValue) => gcj02ToWgs84([point.getLng(), point.getLat()]));
      const existing = document.navigation[`${document.trip.id}::${day.id}`];
      onSetNavigation(day.id, { ...existing, routeId: document.trip.id, dayId: day.id, name: day.name, status: "manual", coordinateSystem: "WGS-84", path, profile: undefined, peaks: undefined, elevationStatus: undefined, elevationSource: undefined, elevationUpdatedAt: undefined });
      polylineEditorRef.current = null;
      setEditingTrack(false);
      return;
    }
    const editor = new AMap.PolylineEditor(map, line);
    polylineEditorRef.current = editor;
    editor.open();
    setEditingTrack(true);
  };

  const plan = useCallback(async () => {
    const AMap = amapRef.current;
    if (!AMap || !day || day.stops.length < 2) return;
    const run = ++planRunRef.current;
    setPlanning(true);
    setPlanProgress(0);
    setMapError(null);
    try {
      const mode: TravelMode = day.mode ?? "driving";
      let distance = 0;
      let duration = 0;
      let path: Array<[number, number]> = [];
      const segments: NavigationSegment[] = [];
      const existing = document.navigation[`${document.trip.id}::${day.id}`];
      for (let index = 1; index < day.stops.length; index++) {
        const fromNode = day.stops[index - 1].node;
        const toNode = day.stops[index].node;
        const fromCoord = document.trip.nodes[fromNode]?.coord;
        const toCoord = document.trip.nodes[toNode]?.coord;
        if (!fromCoord || !toCoord) throw new RoutePlanError("地点坐标不存在");
        const reusable = existing?.segments?.find((segment) => segment.fromNode === fromNode && segment.toNode === toNode && segment.mode === mode && sameCoord(segment.fromCoord, fromCoord) && sameCoord(segment.toCoord, toCoord) && !!segment.path?.length);
        let segment: NavigationSegment;
        if (reusable) {
          segment = reusable;
        } else {
          const start = wgs84ToGcj02(fromCoord);
          const end = wgs84ToGcj02(toCoord);
          const result = await requestRoute(routeRequestKey(mode, start, end), () => planSegment(AMap, mode, start, end));
          segment = { fromNode, toNode, mode, fromCoord, toCoord, distanceMeters: result.distance, durationSeconds: result.duration, path: result.path.map(gcj02ToWgs84) };
        }
        if (run !== planRunRef.current) return;
        distance += segment.distanceMeters;
        duration += segment.durationSeconds;
        path = appendPath(path, segment.path ?? []);
        segments.push(segment);
        setPlanProgress(index);
      }
      if (run === planRunRef.current) onSetNavigation(day.id, { routeId: document.trip.id, dayId: day.id, name: day.name, status: "complete", distanceMeters: distance, durationSeconds: duration, segments, policy: modeLabel(mode), coordinateSystem: "WGS-84", path, profile: undefined, peaks: undefined });
    } catch (error) {
      if (run !== planRunRef.current) return;
      if (error instanceof RoutePlanError && error.code === "10021") {
        setCooldownSeconds(Math.max(1, Math.ceil(getRouteCooldownMs() / 1000)));
        setMapError("高德路线请求过快，已暂停自动重算 30 秒。原道路轨迹已保留，稍后只重试尚未完成的路段。");
      } else {
        setMapError(error instanceof RoutePlanError && error.message !== "route unavailable" ? error.message : "道路规划没有返回可用路线，请检查地点或更换出行方式");
      }
    } finally {
      if (run === planRunRef.current) setPlanning(false);
    }
  }, [day, document.navigation, document.trip.id, document.trip.nodes, onSetNavigation]);

  useEffect(() => {
    if (!day) return;
    const previous = routeSignaturesRef.current[day.id];
    routeSignaturesRef.current[day.id] = routeSignature;
    if (previous && previous !== routeSignature) setRoutePlanVersion((value) => value + 1);
  }, [day?.id, routeSignature]);

  useEffect(() => {
    if (!routePlanVersion || !canEdit || !ready || !day || day.stops.length < 2) return;
    const timer = window.setTimeout(() => void plan(), 900);
    return () => window.clearTimeout(timer);
  }, [canEdit, day?.id, ready, routePlanVersion, routeSignature]);

  useEffect(() => {
    if (!canEdit || !day) return;
    const navigation = document.navigation[`${document.trip.id}::${day.id}`];
    if (!navigation || !["complete", "manual"].includes(navigation.status) || navigation.path.length < 2 || (navigation.profile?.length ?? 0) >= 2 || navigation.elevationStatus === "failed") return;
    const samples = sampleRoutePath(navigation.path);
    if (samples.length < 2) return;
    const fingerprint = routeFingerprint(navigation.path);
    if (activeElevationPathsRef.current[day.id] === fingerprint) return;
    activeElevationPathsRef.current[day.id] = fingerprint;
    const run = (elevationRunsRef.current[day.id] ?? 0) + 1;
    elevationRunsRef.current[day.id] = run;
    onSetNavigation(day.id, { ...navigation, elevationStatus: "loading", elevationSource: undefined, elevationUpdatedAt: undefined });
    void api.elevationProfile(samples.map((sample) => sample.coord)).then((result) => {
      const currentDocument = documentRef.current;
      const current = currentDocument.navigation[`${currentDocument.trip.id}::${day.id}`];
      if (elevationRunsRef.current[day.id] !== run || !current || routeFingerprint(current.path) !== fingerprint || result.elevations.length !== samples.length) return;
      const profile = samples.map((sample, index) => ({ ...sample, elevation: result.elevations[index] }));
      onSetNavigation(day.id, { ...current, profile, peaks: buildElevationPeaks(profile), elevationStatus: "complete", elevationSource: result.source, elevationUpdatedAt: result.queriedAt });
    }).catch(() => {
      const currentDocument = documentRef.current;
      const current = currentDocument.navigation[`${currentDocument.trip.id}::${day.id}`];
      if (elevationRunsRef.current[day.id] !== run || !current || routeFingerprint(current.path) !== fingerprint) return;
      onSetNavigation(day.id, { ...current, elevationStatus: "failed", profile: undefined, peaks: undefined });
    }).finally(() => {
      if (activeElevationPathsRef.current[day.id] === fingerprint) delete activeElevationPathsRef.current[day.id];
    });
  }, [canEdit, day?.id, document.navigation, document.trip.id, onSetNavigation]);

  useEffect(() => {
    if (!cooldownSeconds) return;
    const timer = window.setInterval(() => setCooldownSeconds(Math.ceil(getRouteCooldownMs() / 1000)), 1000);
    return () => window.clearInterval(timer);
  }, [cooldownSeconds > 0]);

  return (
    <section className={`map-stage ${canEdit ? "is-editing" : "is-browsing"}${addingPoint ? " is-adding" : ""}`} aria-label="路线地图">
      <div ref={containerRef} className="map-canvas" />
      {!config?.amapKey && <div className="map-fallback"><Crosshair size={34} /><strong>地图等待配置</strong><span>{canEdit ? "设置 AMAP_KEY 后即可搜索、点选和规划道路；左侧行程编辑不受影响。" : "设置 AMAP_KEY 后即可在地图上浏览道路与地点；当前仍可通过左侧查看逐日行程。"}</span></div>}

      {showSearch && <form className="map-search" onSubmit={(event) => { event.preventDefault(); search(); }}>
        <Search size={19} aria-hidden="true" />
        <input name="poi-query" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="搜索景点、城镇、酒店或地址" aria-label="搜索地点" />
        <select name="poi-scope" value={searchScope} onChange={(event) => setSearchScope(event.target.value as "global" | "viewport")} aria-label="搜索范围"><option value="viewport">地图附近</option><option value="global">全国</option></select>
        {query && <button type="button" className="icon-button compact" onClick={() => { setQuery(""); setResults([]); }} aria-label="清除搜索"><X size={16} /></button>}
        <button className="search-action" disabled={searching || query.trim().length < 2}>{searching ? "搜索中" : "搜索"}</button>
        {!!results.length && <div className="search-results">{results.map((poi, index) => <button key={poi.id || `${poi.name}-${index}`} type="button" onClick={() => choosePoi(poi)}><MapPinPlus size={18} /><span><strong>{poi.name}</strong><small>{[poi.address, poi.type].filter(Boolean).join(" · ") || "地点"}</small></span></button>)}</div>}
      </form>}

      <div className="map-context-bar">
        {canEdit && <div className="map-mode-badge is-editing"><Pencil size={13} /><span>编辑模式</span></div>}
        <div className="map-view-switch" role="group" aria-label="地图路线范围">
          <button className={viewScope === "day" ? "is-active" : ""} onClick={() => onViewScopeChange("day")} aria-pressed={viewScope === "day"}>当天</button>
          <button className={viewScope === "all" ? "is-active" : ""} onClick={() => onViewScopeChange("all")} aria-pressed={viewScope === "all"}>全程</button>
        </div>
      </div>

      <div className="map-utility-tools" aria-label="地图定位工具">
        <button onClick={locate} title="定位当前位置" aria-label="我的位置"><LocateFixed size={18} /></button>
        <button onClick={fit} title="适配当前可见路线" aria-label="适配路线"><Crosshair size={18} /></button>
      </div>

      {canEdit && <div className="map-edit-tools" aria-label="编辑工具">
        <span className="map-edit-tools__label">编辑工具</span>
        <button className={addingPoint ? "is-active" : ""} onClick={() => setAddingPoint((value) => !value)} title="在地图上添加地点"><MapPinPlus size={18} /><span>{addingPoint ? "点击地图放置" : "添加地点"}</span></button>
        <button onClick={() => void plan()} disabled={planning || cooldownSeconds > 0 || !day || day.stops.length < 2} title={cooldownSeconds ? `请求限流冷却中，${cooldownSeconds} 秒后可重试` : "按当前顺序重新规划道路"}><RefreshCw size={18} className={planning ? "spin" : ""} /><span>{planning ? `计算 ${planProgress}/${Math.max(1, (day?.stops.length ?? 1) - 1)}` : cooldownSeconds ? `${cooldownSeconds} 秒后重试` : "重算道路"}</span></button>
        <button className={editingTrack ? "is-active" : ""} onClick={toggleTrackEditing} disabled={!hasEditableLine} title="高级模式：手工调整道路折线"><PencilRuler size={18} /><span>{editingTrack ? "保存轨迹" : "微调轨迹"}</span></button>
      </div>}

      {addingPoint && <div className="map-hint"><MapPinPlus size={17} />点击地图放置新地点，按 Esc 取消</div>}
      {mapError && <div className="map-notice"><span>{mapError}</span><button onClick={() => setMapError(null)} aria-label="关闭"><Check size={16} /></button></div>}
    </section>
  );
}

function planSegment(AMap: AMapValue, mode: TravelMode, start: [number, number], end: [number, number]) {
  return new Promise<RouteResult>((resolve, reject) => {
    const Planner = mode === "walking" ? AMap.Walking : mode === "riding" ? AMap.Riding : AMap.Driving;
    const planner = new Planner(mode === "driving" ? { policy: 0, showTraffic: false, hideMarkers: true } : {});
    planner.search(start, end, (status: string, result: AMapValue) => {
      const route = result?.routes?.[0];
      if (status !== "complete" || !route) return reject(new RoutePlanError(String(result?.info || "route unavailable"), String(result?.infocode || "")));
      const steps = route.steps ?? route.rides ?? [];
      const path = steps.flatMap((step: AMapValue) => (step.path ?? []).map((point: AMapValue) => [point.lng, point.lat] as [number, number]));
      resolve({ distance: Number(route.distance) || 0, duration: Number(route.time) || 0, path });
    });
  });
}

function sameCoord(a?: [number, number], b?: [number, number]) {
  return !!a && !!b && Math.abs(a[0] - b[0]) < 0.000001 && Math.abs(a[1] - b[1]) < 0.000001;
}

function appendPath(target: Array<[number, number]>, addition: Array<[number, number]>) {
  if (!target.length) return [...addition];
  if (!addition.length) return target;
  return sameCoord(target.at(-1), addition[0]) ? [...target, ...addition.slice(1)] : [...target, ...addition];
}

function routeFingerprint(path: Array<[number, number]>) {
  const indexes = [0, Math.floor(path.length / 4), Math.floor(path.length / 2), Math.floor(path.length * 3 / 4), path.length - 1];
  return `${path.length}|${indexes.map((index) => path[index]?.map((value) => value.toFixed(6)).join(",")).join("|")}`;
}

function nearestProfilePoint(profile: NonNullable<Navigation["profile"]>, coord: [number, number]) {
  return profile.reduce<(typeof profile)[number] | undefined>((nearest, point) => {
    if (!point.coord) return nearest;
    if (!nearest?.coord) return point;
    const distance = (point.coord[0] - coord[0]) ** 2 + (point.coord[1] - coord[1]) ** 2;
    const nearestDistance = (nearest.coord[0] - coord[0]) ** 2 + (nearest.coord[1] - coord[1]) ** 2;
    return distance < nearestDistance ? point : nearest;
  }, undefined);
}

function modeLabel(mode: TravelMode) {
  return mode === "walking" ? "高德步行" : mode === "riding" ? "高德骑行" : "高德驾车·速度优先";
}
